import join from 'url-join';

import { http } from '../util';
import { showToast } from '../components/toast/actions';
import { TranslatedCopy } from '../intl';
import { getSelectedAccountId } from './account/selectors';
import { flatten } from '../util/array/flatten';

const addStr = (str, index, stringToAdd) => {
  return str.length === 4 ? str.substring(0, index) + stringToAdd + str.substring(index, str.length) : str;
};

export const handleError = (dispatch, action, error) => {
  dispatch(
    showToast({
      message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
      type: 'error',
      error
    })
  );

  return dispatch(action.error(error));
};

export const handleErrorNoAction = (dispatch, error) =>
  dispatch(
    showToast({
      message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
      type: 'error',
      error
    })
  );

/**
 *
 * @param {array} sortBy Array of objects with id field to sort by and desc field for direction
 * @returns {string} Returns the sort query param
 */
export const generateSortQuery = sortBy => {
  const query = (sortBy || [])
    .filter(({ id }) => id)
    .map(({ id, desc }) => {
      let query = '';

      if (id === 'indexed') query = `hasInvalidProducts${desc ? 'Asc' : 'Desc'},status${desc ? 'Desc' : 'Asc'}`;
      else if (id === 'catalogTypes') query = `catalogTypeName${desc ? 'Desc' : 'Asc'}`;
      else query = `${id}${desc ? 'Desc' : 'Asc'}`;

      return query;
    })
    .join(',');

  return query ? `&sort=${query}` : '';
};

export const getQueryString = ({ queries, values = {} }) => {
  let generatedQuery = '';

  queries?.forEach(query => {
    if (['id', 'name', 'groupName', 'owner'].includes(query))
      generatedQuery += `&filter=${query}Like(${values[query]})`;
    else if (['createTimestamp', 'crdDate'].includes(query))
      generatedQuery += `&filter=${query}(${new Date(values[query]).toISOString()})`;
    else if (['catalogTypes'].includes(query)) generatedQuery += `&filter=catalogTypeNameLike(${values[query]})`;
    else if (['seasons'].includes(query)) {
      const seasonValues = values[query]?.split(',');
      const trimmedValues = seasonValues.map(i => {
        return addStr(i, 2, 20);
      });
      generatedQuery += `&filter=seasonsIncludes(${trimmedValues?.join(',')})`;
    } else if (['orderTypes'].includes(query)) generatedQuery += `&filter=orderTypesIncludes(${values[query]})`;
    else if (['status'].includes(query))
      generatedQuery +=
        values[query] === 'Errors'
          ? '&filter=hasInvalidProducts(true)'
          : `&filter=${query}(${values[query].toUpperCase()})`;
    else if (['directShip'].includes(query))
      generatedQuery +=
        // eslint-disable-next-line no-nested-ternary
        values[query]?.toLowerCase() === 'yes'
          ? '&filter=directShip(true)'
          : values[query]?.toLowerCase() === 'no'
          ? '&filter=directShip(false)'
          : `&filter=directShip(${values[query]})`;
    else generatedQuery += `&filter=${query}(${values[query]})`;
  });

  return generatedQuery;
};

const catalogPropertiesOrder = {
  'catalog id': '',
  salesOrg: '',
  groupName: '',
  'catalog name': '',
  status: '',
  hasInvalidProducts: '',
  catalogTypes: '',
  seasons: '',
  orderTypes: '',
  directShip: '',
  atOnceStart: '',
  atOnceEnd: '',
  futuresStart: '',
  futuresEnd: '',
  futuresCrd: '',
  forecastStart: '',
  forecastEnd: '',
  forecastCrd: '',
  owner: '',
  createdBy: '',
  createTimestamp: '',
  updatedBy: '',
  updateTimestamp: ''
};

export const formatCatalogForCSV = (catalog, id, name) => {
  const objectOrder = { ...catalogPropertiesOrder };
  return Object.assign(objectOrder, {
    ...catalog,
    'catalog id': id,
    'catalog name': name,
    catalogTypes: catalog?.catalogTypes?.map(catalogType => catalogType.name).join(',') || []
  });
};

const productPropertiesOrder = {
  'product id': '',
  'product name': '',
  'product code': '',
  isReturnable: '',
  isInvalid: ''
};

export const formatProductsForCSV = ({ id, name, productCode, returnable, isInvalid }) => {
  const objectOrder = { ...productPropertiesOrder };

  return Object.assign(objectOrder, {
    'product id': id,
    'product name': name,
    'product code': productCode,
    isReturnable: returnable ? 'Yes' : 'No',
    isInvalid: isInvalid ? 'Yes' : 'No'
  });
};

const accountContractPropertiesOrder = {
  sourceContractId: '',
  contractGroupName: '',
  poNumber: '',
  beginDate: '',
  endDate: '',
  availOnGameday: '',
  createdBy: '',
  createTimestamp: '',
  updatedBy: '',
  updateTimestamp: ''
};

const availOnGamedayValueMap = {
  true: 'yes',
  false: 'no',
  null: ''
};

export const formatAccountContractForCSV = (contract, contractGroup, availOnGameday) => {
  const objectOrder = { ...accountContractPropertiesOrder };
  return Object.assign(objectOrder, {
    ...contract,
    contractGroupName: contractGroup?.name,
    availOnGameday: availOnGamedayValueMap[availOnGameday],
    departments: contract.departments?.map(dept => `${dept.number} ${dept.name}`).join(', ')
  });
};

// Utility to assist with requests for all entity data
// Adjust accordingly
export const aggregateDataByEntity =
  ({
    baseName,
    entity,
    fields,
    constant = 25,
    callback = array => array,
    forSelectedAccount = false,
    filters = {},
    sortBy = []
  }) =>
  async (dispatch, getState) => {
    if (forSelectedAccount && !getSelectedAccountId(getState())) {
      return Promise.resolve();
    }

    dispatch({ type: `${baseName}_START` });

    let queryString = '';
    const queries = Object.keys(filters);

    if (queries.length) {
      queryString += getQueryString({ queries, values: filters });
    }

    if (sortBy.length) {
      queryString += generateSortQuery(sortBy?.length ? sortBy : [{ id: 'createTimestamp', desc: true }]);
    }

    const api2 = join(import.meta.env.VITE_API2_URL, 'v1');
    const url = `/${entity}${
      forSelectedAccount ? `?filter=accountId(${getSelectedAccountId(getState())})${queryString}` : ''
    }${forSelectedAccount ? '&' : '?'}count=${constant}${fields ? `&fields=${fields}` : ''}`;
    const aggregatedResponses = [];

    try {
      const {
        data: {
          pages: { totalPages },
          objects
        }
      } = await http.get(join(api2, url));

      aggregatedResponses.push(callback(objects));

      if (totalPages > 1) {
        let anchor = constant;
        const subsequentRequests = [];

        for (let i = 2; i <= totalPages; i++) {
          subsequentRequests.push(http.get(join(api2, `${url}&anchor=${anchor}`)));
          anchor += constant;
        }

        const subsequentResponses = await Promise.all(subsequentRequests);

        for (let i = 0; i < subsequentResponses.length; i++) {
          aggregatedResponses.push(callback(subsequentResponses[i].data.objects));
        }
      }
    } catch (error) {
      dispatch({ type: `${baseName}_ERROR`, error });
    }

    return dispatch({
      type: `${baseName}_SUCCESS`,
      payload: { [entity]: flatten(aggregatedResponses) }
    });
  };

export const formatSeasonRange = seasons => {
  const trimmedValues = seasons?.map(i => {
    return i?.slice(0, 2) + i?.slice(4);
  });
  let finalValue = '';
  finalValue =
    trimmedValues.length === 0
      ? ''
      : `${trimmedValues[0]}${trimmedValues.length > 1 ? ` - ${trimmedValues[trimmedValues.length - 1]}` : ''}`;

  return finalValue;
};

export const escapeSpecialCharsInFilterInput = input => {
  const escapeChars = '[]()'; // characters that need to be escaped
  const backslash = '%5C';

  return input
    .split('')
    .map(char => (escapeChars.includes(char) ? `${backslash}${char}` : encodeURIComponent(char)))
    .join('');
};

export const escapeSpecialCharsAndHyphenInFilterInput = input => {
  const escapeChars = '[]()'; // characters that need to be escaped
  const backslash = '%5C';

  return input
    .replace(/-/, '')
    .split('')
    .map(char => (escapeChars.includes(char) ? `${backslash}${char}` : encodeURIComponent(char)))
    .join('');
};
